/**
 * Canonical post-status helpers.
 *
 * Centralizes STATUS_DOT_COLORS, STATUS_LABELS, and the v5 status-aware
 * primary-CTA lookup that drives the PostBody inline toolbar + the
 * PostInspectorShowcase mock. Three call sites used to triplicate the
 * same constants (PostBody, UnifiedPostDialog, SocialPostCard); the
 * showcase added a 4th near-duplicate.
 *
 * One file, one truth — every consumer imports from here.
 */
import { ArrowSquareOutIcon, CalendarIcon, PaperPlaneRightIcon } from '@phosphor-icons/react';
import type { ComponentType } from 'react';

// Mirrors the social_posts.status CHECK enum (plus the legacy UI-only
// 'queued'). The publishing / partially_published / failed_connection_removed
// states are real DB values written by the publish pipeline — before F-135
// they fell through to a grey dot + raw "partially_published" string.
export type PostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  // partially_scheduled = in-flight staggered post: ≥1 platform already fired,
  // ≥1 still pending at its own later time. Distinct from partially_published
  // (terminal). See docs/superpowers/specs/2026-07-04-per-platform-scheduling-design.md.
  | 'partially_scheduled'
  | 'partially_published'
  | 'failed'
  | 'failed_connection_removed'
  | 'queued';

/** Tailwind class for the small 1.5×1.5 status dot rendered before STATUS_LABELS text. */
export const STATUS_DOT_COLORS: Record<string, string> = {
  draft: 'bg-muted-foreground/40',
  scheduled: 'bg-foreground',
  publishing: 'bg-warning',
  published: 'bg-success',
  partially_scheduled: 'bg-warning',
  partially_published: 'bg-warning',
  failed: 'bg-destructive',
  failed_connection_removed: 'bg-destructive',
  queued: 'bg-warning',
};

/** Sentence-case status labels. */
export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  publishing: 'Publishing…',
  published: 'Published',
  partially_scheduled: 'Partly sent',
  partially_published: 'Partly sent',
  failed: 'Failed',
  failed_connection_removed: 'Connection removed',
  queued: 'Queued',
};

type PrimaryCTAIconProps = {
  className?: string;
  weight?: 'regular' | 'fill' | 'bold';
};

interface PrimaryCTASpec {
  label: string;
  Icon: ComponentType<PrimaryCTAIconProps>;
}

/**
 * v5 status-aware right primary CTA — what the user most likely wants
 * to do next given the current status.
 *
 * Source of truth shared by PostBody (production toolbar) and the
 * PostInspectorShowcase (canonical demonstration).
 */
export const STATUS_PRIMARY_CTA: Record<PostStatus, PrimaryCTASpec> = {
  draft: { label: 'Approve & schedule', Icon: PaperPlaneRightIcon },
  scheduled: { label: 'Reschedule', Icon: CalendarIcon },
  publishing: { label: 'Publishing…', Icon: PaperPlaneRightIcon },
  published: { label: 'View live', Icon: ArrowSquareOutIcon },
  // In-flight: some platforms are live, others still scheduled — the honest
  // action is to view what's already out (not "reschedule", which would risk
  // the live ones).
  partially_scheduled: { label: 'View live', Icon: ArrowSquareOutIcon },
  partially_published: { label: 'View live', Icon: ArrowSquareOutIcon },
  failed: { label: 'Retry', Icon: PaperPlaneRightIcon },
  failed_connection_removed: { label: 'Reconnect', Icon: PaperPlaneRightIcon },
  queued: { label: 'View queue', Icon: CalendarIcon },
};

const PARTLY_SENT_STATUSES = ['partially_published', 'partially_scheduled'] as const;

export function expandStatusFilter(selected: readonly string[] | null | undefined): Set<string> {
  const statuses = new Set<string>(selected ?? []);
  if (PARTLY_SENT_STATUSES.some((status) => statuses.has(status))) {
    for (const status of PARTLY_SENT_STATUSES) statuses.add(status);
  }
  return statuses;
}
